<script setup>
/**
 * "Recently removed" (P-6.11): what was removed in the last 30 days, with Put it back. One list
 * for the whole account comes from the BFF; each page shows the kinds it owns (Answers: sources
 * and answers; Tasks: tasks, or menus on its Menus tab; Your systems: connections).
 *
 * Putting one back costs no credits: what the assistant learned comes back with it. A task,
 * menu or connection may come back switched off, or renamed when its name was taken since,
 * and the alert says which.
 *
 * Renders nothing unless it has a row, for the reason `RestoreHistory.vue` gives.
 */
import { computed, ref } from 'vue';

import { useAlert } from 'dashboard/composables';
import Banner from 'dashboard/components-next/banner/Banner.vue';
import Button from 'dashboard/components-next/button/Button.vue';
import SettingsHeader from 'dashboard/components-next/captain/pageComponents/settings/SettingsHeader.vue';

import { useWasloI18n } from '../i18n.js';
import { useWasloApi } from '../composables/useWasloApi.js';
import { relativeTime } from '../helpers/relative-time.js';
import { wasloApi } from '../api/client.js';

const props = defineProps({
  accountId: { type: Number, default: null },
  /** Which kinds this page shows: `source`, `answer`, `task`, `menu`, `connection`. */
  kinds: { type: Array, required: true },
});

const emit = defineEmits(['restored']);

const { t, locale } = useWasloI18n();

const removed = useWasloApi(
  signal =>
    props.accountId === null
      ? Promise.resolve([])
      : wasloApi.getRemoved(props.accountId, signal),
  { isEmpty: () => false }
);

const rows = computed(() =>
  (Array.isArray(removed.data.value) ? removed.data.value : []).filter(row =>
    props.kinds.includes(row.kind)
  )
);

const busyKey = ref(null);
const failure = ref('');

const keyOf = row => `${row.kind}:${row.id}`;
const whenOf = at =>
  relativeTime(at, locale.value, {
    justNow: t('WASLO.UNDO.REMOVED.JUST_NOW'),
  }) ?? '';
const whoOf = by => t(`WASLO.UNDO.WHO.${String(by).toUpperCase()}`);
const kindOf = kind =>
  t(`WASLO.UNDO.REMOVED.KIND.${String(kind).toUpperCase()}`);

const putBack = async row => {
  if (busyKey.value !== null || props.accountId === null) return;
  busyKey.value = keyOf(row);
  failure.value = '';
  try {
    const result = await wasloApi.putBack(props.accountId, row.kind, row.id);
    let message = 'WASLO.UNDO.REMOVED.PUT_BACK_DONE';
    if (result.renamed) message = 'WASLO.UNDO.REMOVED.PUT_BACK_RENAMED';
    else if (result.switchedOff) message = 'WASLO.UNDO.REMOVED.PUT_BACK_OFF';
    useAlert(t(message));
    emit('restored', result);
    await removed.reload();
  } catch (error) {
    failure.value = t(
      error?.status === 409
        ? 'WASLO.UNDO.REMOVED.CONFLICT'
        : 'WASLO.UNDO.REMOVED.FAILED'
    );
  } finally {
    busyKey.value = null;
  }
};

defineExpose({ reload: () => removed.reload() });
</script>

<template>
  <section
    v-if="rows.length"
    class="flex flex-col gap-4"
    data-waslo-recently-removed
  >
    <SettingsHeader
      :heading="t('WASLO.UNDO.REMOVED.HEADING')"
      :description="t('WASLO.UNDO.REMOVED.DESCRIPTION')"
    />
    <Banner v-if="failure" color="ruby" data-waslo-recently-removed-error>
      {{ failure }}
    </Banner>
    <ul
      class="flex flex-col w-full divide-y outline outline-1 -outline-offset-1 divide-n-weak outline-n-container rounded-xl bg-n-solid-2"
    >
      <li
        v-for="row in rows"
        :key="keyOf(row)"
        class="flex flex-wrap items-center justify-between gap-3 px-4 py-3"
        :data-waslo-removed-row="keyOf(row)"
      >
        <div class="flex flex-col min-w-0 gap-1">
          <p class="mb-0 text-sm font-medium break-words text-n-slate-12">
            <bdi>{{ row.label }}</bdi>
          </p>
          <p class="mb-0 text-xs text-n-slate-11">
            {{ kindOf(row.kind) }} ·
            {{
              t('WASLO.UNDO.REMOVED.ROW', {
                when: whenOf(row.removedAt),
                who: whoOf(row.by),
              })
            }}
          </p>
        </div>
        <Button
          :label="t('WASLO.UNDO.REMOVED.PUT_BACK')"
          size="sm"
          variant="faded"
          color="slate"
          icon="i-lucide-rotate-ccw"
          :is-loading="busyKey === keyOf(row)"
          data-waslo-put-back
          @click="putBack(row)"
        />
      </li>
    </ul>
  </section>
</template>
